import * as QueryKeys from "data";
import { Alert, Card, CardHeader, Skeleton } from "@mui/material";
import CardContent from "@mui/material/CardContent";
import { UsageState } from "contexts/models";
import { useIdentity } from "contexts/identity-context";
import { DateRange } from "contexts/models";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import * as echarts from "echarts";

import { useEffect, useRef, useState } from "react";
import useDarkMode from "use-dark-mode";
import { darkTheme } from "./dark";
import {
  EChartsOptions,
  extractEChartData,
  getDisplayTimeFormat,
  timePeriodFromDateRange,
  unixDurationToDateRange,
} from "./utils-usage";
import { getActiveOrganisation } from "contexts/identity-context";
import { fetchApiKeys, fetchUsage } from "data/queries";
import { useQuery } from "react-query";
import { resolveIdentityId } from "utils/identity";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";

//  TODO , ADD ERROR MESSAGES

interface UsageGraphProps {
  startTime: number;
  endTime: number;
}

export const UsageGraph = (props: UsageGraphProps) => {
  echarts.registerTheme("darkTheme", darkTheme);
  const darkMode = useDarkMode(false);

  // useQuery
  const [identityState] = useIdentity();
  const isUser = !getActiveOrganisation(identityState);
  const identityId = resolveIdentityId(identityState, isUser);

  const duration = unixDurationToDateRange(props.startTime, props.endTime);

  const usageQuery = useQuery(
    [QueryKeys.usageGraph, identityId, props.startTime, props.endTime],
    () => fetchUsage(isUser, identityState, props.startTime, props.endTime),
    {
      onSuccess: (queryData) => {
        setUsageGraph((prevState) => {
          return {
            ...prevState,
            startTime: props.startTime,
            endTime: props.endTime,
            usage: queryData,
            duration: duration,
          };
        });
      },
    }
  );

  const apiKeysQuery = useQuery([QueryKeys.apiKeys, identityId], () =>
    fetchApiKeys(isUser, identityState)
  );

  const isLoading = usageQuery.isLoading || apiKeysQuery.isLoading;
  const isError = usageQuery.isError || apiKeysQuery.isError;
  const isSuccess = usageQuery.isSuccess && apiKeysQuery.isSuccess;

  const initialDailyUsageState: UsageState = {
    startTime: props.startTime,
    endTime: props.endTime,
    usage: usageQuery.data,
    duration: duration,
  };

  const [usageGraph, setUsageGraph] = useState(initialDailyUsageState);

  const displayTimeformat = getDisplayTimeFormat(usageGraph.duration);

  dayjs.extend(customParseFormat);
  dayjs.extend(advancedFormat);

  const chartRef = useRef(null);

  useEffect(() => {
    if (isSuccess) {
      //@ts-ignore
      let chartInstance = echarts.getInstanceByDom(chartRef.current);

      if (!chartInstance) {
        chartInstance = echarts.init(
          chartRef.current,
          darkMode.value ? darkTheme : undefined
        );
      }
      const option = EChartsOptions(
        extractEChartData(usageGraph, apiKeysQuery, isLoading, isError),
        displayTimeformat,
        usageQuery.data.intervalHours,
        isLoading,
        isError
      );

      chartInstance.setOption({ ...option, notMerge: true });

      // Cleanup on unmount
      return () => {
        chartInstance.dispose();
      };
    }
  }, [isSuccess]);

  return (
    <Card>
      <CardHeader title="Credit Usage" />
      <CardContent>
        {isLoading && (
          <>
            <Skeleton variant="rectangular" width="100%" height="100px" />
            <br />
            <Skeleton variant="rectangular" width="100%" height="300px" />
          </>
        )}

        <ReactQueryErrorWrapper
          queries={[usageQuery, apiKeysQuery]}
          mutations={[]}
        />

        {isSuccess && (
          <>
            <div ref={chartRef} style={{ height: 500 }} />
          </>
        )}
      </CardContent>
    </Card>
  );
};
